import axios from 'axios';
import { filterTombstoned } from '../lib/tombstones';
import {
  AUTH_401_DETAILS, SessionEndedError, captureDeviceToken, deviceHeaders, endSession, ensureAccessToken,
  getAccessToken, refreshSession,
} from './session';

// Trim any trailing slash on the backend URL — otherwise concatenating
// `/api` produces a double slash, which K8s ingress treats as a static
// asset path and serves the React index.html instead of routing to FastAPI.
const RAW_BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const BASE = `${RAW_BACKEND.replace(/\/+$/, '')}/api`;

const api = axios.create({ baseURL: BASE, withCredentials: true });

const isSessionEndpoint = (url = '') => /\/auth\/(login|refresh|logout|session\/upgrade)/.test(url);

api.interceptors.request.use(async (config) => {
  const token = isSessionEndpoint(config.url) ? getAccessToken() : await ensureAccessToken();
  if (token && !config.headers.Authorization) config.headers.Authorization = `Bearer ${token}`;
  if (isSessionEndpoint(config.url) || /\/auth\/(invite|reset-password)\//.test(config.url || '')) {
    Object.assign(config.headers, deviceHeaders());
  }
  // iOS Safari (and some corporate proxies) aggressively cache GET
  // responses with no cache headers. That was the source of "I deleted it
  // but it's still there after refresh" bug reports — the list endpoint
  // was served from cache, so the deleted row reappeared. Force no-cache
  // + a per-request timestamp so every GET hits the server.
  if ((config.method || 'get').toLowerCase() === 'get') {
    config.headers['Cache-Control'] = 'no-cache';
    config.headers.Pragma = 'no-cache';
    config.params = { ...(config.params || {}), _t: Date.now() };
  }
  return config;
});

// On an auth 401, refresh once and replay. Non-session 401s (e.g. an
// expired Xero token) carry other details and pass straight through.
api.interceptors.response.use(
  (r) => { captureDeviceToken(r.headers?.['x-lutrom-device-token']); return r; },
  async (err) => {
    const cfg = err?.config;
    const detail = err?.response?.data?.detail;
    const isAuth401 = err?.response?.status === 401 && AUTH_401_DETAILS.includes(detail);
    if (!cfg || !isAuth401 || cfg._retried || isSessionEndpoint(cfg.url) || !getAccessToken()) {
      return Promise.reject(err);
    }
    cfg._retried = true;
    try {
      await refreshSession();
    } catch (e) {
      if (!(e instanceof SessionEndedError)) return Promise.reject(err);
      endSession(e.detail);
      // The app is redirecting to login; keep callers pending instead of surfacing stale 401s.
      return new Promise(() => {});
    }
    cfg.headers.Authorization = `Bearer ${getAccessToken()}`;
    return api(cfg);
  }
);

// Auth
export const apiLogin = (email, password) => api.post('/auth/login', { email, password }).then(r => r.data);
export const apiMe = () => api.get('/auth/me').then(r => r.data);
// Admin-only: mint a session for another user (customer only, enforced
// server-side). Used by the "View portal as customer" flow so support
// can literally see what the customer sees without a shared password.
export const apiImpersonate = (userId) => api.post(`/auth/impersonate/${userId}`).then(r => r.data);
export const listMySessions = () => api.get('/auth/sessions').then(r => r.data);
export const revokeOtherSessions = () => api.post('/auth/sessions/revoke-others').then(r => r.data);
export const forceSignOutUser = (userId) => api.post(`/users/${userId}/revoke-sessions`).then(r => r.data);
export const setUserDisabled = (userId, disabled) => api.patch(`/users/${userId}/status`, { disabled }).then(r => r.data);

// Admin-only: login activity audit log.
// `listLoginEvents` returns `{events, truncated}`; `getLoginEventsSummary`
// returns the counters shown on the summary strip.
export const listLoginEvents = (params = {}) =>
  api.get('/admin/login-events', { params }).then(r => r.data);
export const getLoginEventsSummary = (days = 30) =>
  api.get('/admin/login-events/summary', { params: { days } }).then(r => r.data);

// Forgot / reset password — public, no auth.
// `apiForgotPassword` always resolves to `{ok: true}` even for unknown
// emails (server-side anti-enumeration). Callers should surface a
// generic "check your inbox" success message either way.
export const apiForgotPassword = (email) => api.post('/auth/forgot-password', { email }).then(r => r.data);
export const apiLookupResetToken = (token) => api.get(`/auth/reset-password/${token}`).then(r => r.data);
export const apiRedeemResetToken = (token, password) => api.post(`/auth/reset-password/${token}`, { password }).then(r => r.data);

// Admin-only: mail a password-reset link to a specific user (for the
// "customer can't log in" phone-support flow).
export const sendUserPasswordReset = (userId) => api.post(`/users/${userId}/send-password-reset`).then(r => r.data);

// Work-order e-signature sign-off. Sends the canvas as an
// `image/png` data URL — backend rejects any other format for
// audit-safety (SVG could smuggle executable content).
export const signOffWorkOrder = (woId, signatureDataUrl) =>
  api.post(`/work-orders/${woId}/sign-off`, { signature: signatureDataUrl }).then(r => r.data);

// PDF preflight — read-only sanity check that reports what each PDF
// section will actually render, plus a list of soft warnings the
// admin can fix before hitting Download.
export const getWorkOrderPdfPreflight = (woId) =>
  api.get(`/work-orders/${woId}/pdf/preflight`).then(r => r.data);

// Admin-only: give an embedded customer contact portal access.
// Creates a sub-user under the customer + sends an invite, or (if the
// user already exists) sends them a password reset. Response includes
// `status: 'invited' | 'reset'` and an `actionUrl` for clipboard fallback.
export const inviteCustomerContact = (customerId, contactIdOrEmail) =>
  api.post(`/customers/${encodeURIComponent(customerId)}/contacts/${encodeURIComponent(contactIdOrEmail)}/portal-invite`).then(r => r.data);
export const getContactPortalStatus = (customerId) =>
  api.get(`/customers/${encodeURIComponent(customerId)}/contact-portal-status`).then(r => r.data);

// Users
export const getUsers = () => api.get('/users').then(r => r.data);
export const createUser = (data) => api.post('/users', data).then(r => r.data);
export const updateUser = (id, data) => api.patch(`/users/${id}`, data).then(r => r.data);
export const deleteUser = (id) => api.delete(`/users/${id}`).then(r => r.data);
export const resendUserInvite = (id) => api.post(`/users/${id}/resend-invite`).then(r => r.data);
export const getInviteLink = (id) => api.get(`/users/${id}/invite-link`).then(r => r.data);

// Sites
export const getSites = () => api.get('/sites').then(r => r.data);
export const getPublicSite = (siteId, assetId) => api.get(`/public/sites/${siteId}`, { params: assetId ? { asset: assetId } : {} }).then(r => r.data);
export const createSite = (data) => api.post('/sites', data).then(r => r.data);
export const updateSite = (id, data) => api.patch(`/sites/${id}`, data).then(r => r.data);
export const updateSiteSafetyDoc = (id, data) => api.patch(`/sites/${id}/safety-doc`, data).then(r => r.data);
export const deleteSite = (id) => api.delete(`/sites/${id}`).then(r => r.data);

// Assets
export const getAssets = () => api.get('/assets').then(r => r.data);
export const createAsset = (data) => api.post('/assets', data).then(r => r.data);
export const updateAsset = (id, data) => api.patch(`/assets/${id}`, data).then(r => r.data);
export const deleteAsset = (id) => api.delete(`/assets/${id}`).then(r => r.data);
export const uploadAssetPhoto = (id, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post(`/assets/${id}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
};
export const deleteAssetPhoto = (id) => api.delete(`/assets/${id}/photo`).then(r => r.data);
export const getCustomerWorkOrder = (id) => api.get(`/customer/work-orders/${id}`).then(r => r.data);
export const listWorkOrderRams = (id) => api.get(`/work-orders/${id}/rams`).then(r => r.data);
export const workOrderRamsPdfUrl = (woId, docId) => {
  // Backend serves the RAMS PDF at this endpoint. Auth cookie / token
  // is included by the axios instance so a plain `<a href>` won't
  // work — callers use `fetchInvoicePdfUrl`-style blob-URL helpers.
  return `${BASE}/work-orders/${woId}/rams/${docId}/pdf`;
};
export const fetchWorkOrderRamsPdfBlob = async (woId, docId) => {
  // Same auth-injection dance as `fetchInvoicePdfUrl` — we grab the
  // PDF bytes with the auth header attached, then hand back a blob
  // URL the browser can open in a new tab.
  const res = await api.get(`/work-orders/${woId}/rams/${docId}/pdf`, { responseType: 'blob' });
  return URL.createObjectURL(res.data);
};
export const bulkImportAssets = (file, dryRun = false) => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('dryRun', dryRun ? '1' : '0');
  return api.post('/assets/bulk-import', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
};

// Procedures / Parts
export const getProcedures = () => api.get('/procedures').then(r => r.data);
export const getParts = () => api.get('/parts').then(r => r.data);

// Inventory - Stores
export const getStores = () => api.get('/stores').then(r => r.data);
export const createStore = (data) => api.post('/stores', data).then(r => r.data);
export const updateStore = (id, data) => api.patch(`/stores/${id}`, data).then(r => r.data);
export const deleteStore = (id) => api.delete(`/stores/${id}`).then(r => r.data);

// Inventory - Locations
export const getLocations = (storeId) => api.get(`/stores/${storeId}/locations`).then(r => r.data);
export const createLocation = (storeId, data) => api.post(`/stores/${storeId}/locations`, data).then(r => r.data);
export const updateLocation = (id, data) => api.patch(`/locations/${id}`, data).then(r => r.data);
export const deleteLocation = (id) => api.delete(`/locations/${id}`).then(r => r.data);

// Inventory - Parts (multi-store, barcodes)
export const getInventoryParts = () => api.get('/inventory/parts').then(r => r.data);
export const getRecentlyPurchasedParts = (limit = 8) =>
  api.get('/inventory/recently-purchased', { params: { limit } }).then(r => r.data);
export const getInventoryPart = (id) => api.get(`/inventory/parts/${id}`).then(r => r.data);
export const lookupBarcode = (code) => api.get(`/inventory/barcode/${encodeURIComponent(code)}`).then(r => r.data);
export const createInventoryPart = (data) => api.post('/inventory/parts', data).then(r => r.data);
export const updateInventoryPart = (id, data) => api.patch(`/inventory/parts/${id}`, data).then(r => r.data);
export const uploadPartPhoto = (id, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post(`/inventory/parts/${id}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
};
export const deletePartPhoto = (id) => api.delete(`/inventory/parts/${id}/photo`).then(r => r.data);
export const bulkUpdateInventoryParts = (payload) =>
  api.post('/inventory/parts/bulk-update', payload).then(r => r.data);
export const seedPartStockFromBill = (id, payload) =>
  api.post(`/inventory/parts/${id}/seed-from-bill`, payload).then(r => r.data);
export const deleteInventoryPart = (id) => api.delete(`/inventory/parts/${id}`).then(r => r.data);
export const setPartStock = (partId, data) => api.post(`/inventory/parts/${partId}/stock`, data).then(r => r.data);
export const adjustPartStock = (partId, data) => api.post(`/inventory/parts/${partId}/adjust`, data).then(r => r.data);
export const transferPartStock = (partId, data) => api.post(`/inventory/parts/${partId}/transfer`, data).then(r => r.data);
export const getPartMovements = (partId) => api.get(`/inventory/parts/${partId}/movements`).then(r => r.data);
export const getInventoryAlerts = () => api.get('/inventory/alerts').then(r => r.data);

// Work-order parts usage (deducts stock)
export const consumePartOnWorkOrder = (woId, data) => api.post(`/work-orders/${woId}/parts-used`, data).then(r => r.data);
export const getWorkOrderPartsUsed = (woId) => api.get(`/work-orders/${woId}/parts-used`).then(r => r.data);

// Defensive ID-dedupe. If a proxy ever replays a response or a race
// condition ever stitches two payloads together (reported once on iOS
// Safari), the UI must still render each WO exactly once. Cheap O(n).
const _dedupeById = (arr) => {
  if (!Array.isArray(arr)) return arr;
  const seen = new Set();
  const out = [];
  for (const x of arr) {
    if (!x?.id || seen.has(x.id)) continue;
    seen.add(x.id);
    out.push(x);
  }
  return out;
};

// Work orders
export const getWorkOrders = () =>
  api.get('/work-orders').then(r => filterTombstoned('work_orders', _dedupeById(r.data)));
export const createWorkOrder = (data) => api.post('/work-orders', data).then(r => r.data);
export const updateWorkOrder = (id, data) => api.patch(`/work-orders/${id}`, data).then(r => r.data);

// Service requests
export const getServiceRequests = () => api.get('/service-requests').then(r => r.data);
export const createServiceRequest = (data) => api.post('/service-requests', data).then(r => r.data);
// Customer-side "request a quote" from the Quotes page — reuses the
// service_requests collection with `kind='quote_request'` so admins
// see it in the same Work Requests inbox, badged as a quote ask.
export const createQuoteRequest = (data) =>
  api.post('/service-requests', { ...data, kind: 'quote_request' }).then(r => r.data);
export const updateServiceRequest = (id, data) => api.patch(`/service-requests/${id}`, data).then(r => r.data);
export const deleteServiceRequest = (id) => api.delete(`/service-requests/${id}`).then(r => r.data);
export const deleteWorkOrder = (id, { forceDetach = false } = {}) =>
  api.delete(`/work-orders/${id}${forceDetach ? '?force_detach=true' : ''}`).then(r => r.data);
// Hard purge — permanently removes the WO document, cannot be undone.
// Used as an escape hatch when soft-delete + tombstone still leaves
// ghost WOs re-appearing after CDN / cache-stale bounces.
export const purgeWorkOrder = (id, { forceDetach = false } = {}) =>
  api.delete(`/work-orders/${id}/purge${forceDetach ? '?force_detach=true' : ''}`).then(r => r.data);
export const deleteSuggestion = (id) => api.delete(`/suggestions/${id}`).then(r => r.data);
export const restoreServiceRequest = (id) => api.post(`/service-requests/${id}/restore`).then(r => r.data);
// Admin one-click conversions from the Work Requests inbox — spawn a
// pre-filled Work Order or draft Quote directly from a request row.
export const createWorkOrderFromServiceRequest = (id) =>
  api.post(`/service-requests/${id}/create-work-order`).then(r => r.data);
export const createQuoteFromServiceRequest = (id) =>
  api.post(`/service-requests/${id}/create-quote`).then(r => r.data);
export const restoreWorkOrder = (id) => api.post(`/work-orders/${id}/restore`).then(r => r.data);
export const restoreSuggestion = (id) => api.post(`/suggestions/${id}/restore`).then(r => r.data);
export const getAdminTimesheets = (params = {}) => api.get('/admin/timesheets', { params }).then(r => r.data);
export const seedTestTimesheets = (body = {}) => api.post('/admin/timesheets/seed-test', body).then(r => r.data);
export const deleteTimesheetDay = (userId, date) => api.delete('/admin/timesheets/day', { params: { userId, date } }).then(r => r.data);
export const getDayTimeEntries = (userId, date) => api.get('/admin/time-entries/day', { params: { userId, date } }).then(r => r.data);
export const adminCreateTimeEntry = (body) => api.post('/admin/time-entries', body).then(r => r.data);
export const adminUpdateTimeEntry = (id, body) => api.patch(`/admin/time-entries/${id}`, body).then(r => r.data);
export const adminRetryBilling = (id) => api.post(`/admin/time-entries/${id}/bill`).then(r => r.data);
export const adminDeleteTimeEntry = (id) => api.delete(`/time-entries/${id}`).then(r => r.data);
export const submitPublicRequest = (data) => api.post('/public/service-requests', data).then(r => r.data);

// Customer self-serve spares
export const getCustomerSpares = () => api.get('/customer/spares').then(r => r.data);
export const takeCustomerSpare = (partId, data) =>
  api.post(`/customer/spares/${partId}/take`, data).then(r => r.data);

// Public operator pickup (no auth)
export const getPublicStore = (storeId) =>
  axios.get(`${BASE}/public/stores/${storeId}`).then(r => r.data);
export const publicStoreTake = (storeId, data) =>
  axios.post(`${BASE}/public/stores/${storeId}/take`, data).then(r => r.data);
export const publicStoreReverseTake = (storeId, movementId, data) =>
  axios.post(`${BASE}/public/stores/${storeId}/movements/${movementId}/reverse`, data).then(r => r.data);

// Suggestions
export const createSuggestion = (data) => api.post('/suggestions', data).then(r => r.data);
export const getMySuggestions = () => api.get('/suggestions/mine').then(r => r.data);
export const customerConfirmSuggestion = (id) => api.post(`/suggestions/${id}/confirm`).then(r => r.data);
export const adminGetSuggestions = () => api.get('/admin/suggestions').then(r => r.data);
export const adminSuggestionAction = (id, action, note = '') =>
  api.post(`/admin/suggestions/${id}/${action}`, { note }).then(r => r.data);

// Reports
export const getReports = () => api.get('/reports').then(r => r.data);
export const getReport = (id) => api.get(`/reports/${id}`).then(r => r.data);
export const getReportPdfUrl = (id) => `${BASE}/reports/${id}/pdf`;
export const getCustomerTimeEntries = () => api.get('/customer/time-entries').then(r => r.data);

// Opens the correct PDF for a customer-facing report row. The row's
// `source` decides which backend endpoint to hit:
//   - certificate → /certificates/{id}/pdf
//   - eicr        → /eicr-reports/{id}/pdf
//   - anything else (uploaded / legacy) → /reports/{id}/pdf
// The PDF is streamed via blob so JWT auth is respected, then opened
// in a new tab so the customer never lands on a "report not found" page.
export const openCustomerReport = async (report) => {
  const token = await ensureAccessToken();
  const source = (report && report.source) || '';
  let path;
  if (source === 'certificate') path = `/certificates/${report.id}/pdf`;
  else if (source === 'eicr') path = `/eicr-reports/${report.id}/pdf`;
  else path = `/reports/${report.id}/pdf`;
  const res = await fetch(`${BASE}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new Error(detail?.detail || 'Failed to open report');
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

export const openInvoicePdf = async (invoiceId) => {
  const token = await ensureAccessToken();
  const res = await fetch(`${BASE}/invoices/${invoiceId}/pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new Error(detail?.detail || 'Failed to generate invoice PDF');
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

// Renders every chase email (one per overdue invoice) — never sends.
export const previewCustomerChase = (id, { to, cc, emails } = {}) =>
  api.post(`/customers/${id}/chase-overdue/preview`, { to, cc, emails }).then(r => r.data);

export const fetchInvoicePdfUrl = (invoiceId) =>
  _fetchPdfAsBlobUrl(`/invoices/${invoiceId}/pdf`);

export const openQuotePdf = async (quoteId) => {
  const token = await ensureAccessToken();
  const res = await fetch(`${BASE}/quotes/${quoteId}/pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new Error(detail?.detail || 'Failed to generate quote PDF');
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

export const fetchQuotePdfUrl = (quoteId) =>
  _fetchPdfAsBlobUrl(`/quotes/${quoteId}/pdf`);

// RAMS / Safety pack PDF — see `fetchRamsPdfUrl` below for the
// preview/download split. Legacy `openRamsPdf` (new-tab open) was
// removed once the in-page preview landed.

// Fetches a PDF and returns { url, filename } so the caller can either
// embed it in an <iframe> (preview) or trigger a download anchor. Callers
// are responsible for eventually revoking the object URL.
const _fetchPdfAsBlobUrl = async (path) => {
  const token = await ensureAccessToken();
  const res = await fetch(`${BASE}${path}${path.includes('?') ? '&' : '?'}_t=${Date.now()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new Error(detail?.detail || 'Failed to fetch PDF');
  }
  const blob = await res.blob();
  // Try to pick up the server-suggested filename from Content-Disposition.
  const disp = res.headers.get('content-disposition') || '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disp);
  const filename = match ? decodeURIComponent(match[1]) : 'document.pdf';
  return { url: URL.createObjectURL(blob), filename };
};

export const fetchRamsPdfUrl = (workOrderId, ramsId) =>
  _fetchPdfAsBlobUrl(`/work-orders/${workOrderId}/rams/${ramsId}/pdf`);

// Single Work Order PDF — Lutrom-branded summary suitable for handover
// or customer record. Uses the active `work_order` template if set,
// otherwise falls back to the hardcoded Lutrom layout.
export const fetchWorkOrderPdfUrl = (workOrderId) =>
  _fetchPdfAsBlobUrl(`/work-orders/${workOrderId}/pdf`);

export const fetchLibraryPdfUrl = (docId) =>
  _fetchPdfAsBlobUrl(`/library/${docId}/pdf`);

export const uploadLibraryFile = (docId, file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post(`/library/${docId}/file`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};
export const deleteLibraryFile = (docId) =>
  api.delete(`/library/${docId}/file`).then(r => r.data);

// Bulk pack uses POST (takes a JSON body of IDs + optional title). Mirrors
// _fetchPdfAsBlobUrl but uses POST with a JSON body.
export const fetchLibraryBulkPackUrl = async (ids, title = null) => {
  const token = await ensureAccessToken();
  const res = await fetch(`${BASE}/library/bulk-pack?_t=${Date.now()}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ids, title }),
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new Error(detail?.detail || 'Failed to build bulk pack');
  }
  const blob = await res.blob();
  const disp = res.headers.get('content-disposition') || '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disp);
  const filename = match ? decodeURIComponent(match[1]) : 'Lutrom-pack.pdf';
  return { url: URL.createObjectURL(blob), filename };
};

// Helper to trigger a browser download for a blob URL with a given filename.
export const triggerDownload = (url, filename) => {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'document.pdf';
  document.body.appendChild(a);
  a.click();
  a.remove();
};

// Document-library bulk pack email — sends the generated booklet straight
// to a customer via Resend. Stamps `packSentAt` / `lastPackSentTo` on
// every included doc for audit.
export const emailLibraryBulkPack = (payload) =>
  api.post('/library/bulk-pack/email', payload).then((r) => r.data);

// Attachments
export const uploadWorkOrderAttachment = (id, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post(`/work-orders/${id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
};
export const uploadServiceRequestAttachment = (id, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post(`/service-requests/${id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
};
export const uploadPublicAttachment = (srId, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return axios.post(`${BASE}/public/service-requests/${srId}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
};
export const fileUrl = (relUrl) => `${process.env.REACT_APP_BACKEND_URL}${relUrl}`;

// Notifications
export const getNotifications = () => api.get('/notifications').then(r => r.data);
export const markNotificationsRead = (ids, all = false) => api.post('/notifications/read', { ids, all }).then(r => r.data);

// Certificates (AI-assisted)
export const aiExtractCertificate = (photoIds, certType, notes, layoutId = null, frameOverrides = null, referenceBrandId = null) =>
  api.post('/certificates/extract', {
    photoIds,
    certType,
    notes,
    ...(layoutId ? { layoutId } : {}),
    ...(frameOverrides ? { frameOverrides } : {}),
    ...(referenceBrandId ? { referenceBrandId } : {}),
  }).then(r => r.data);
export const createCertificate = (data) => api.post('/certificates', data).then(r => r.data);
export const getCertificates = () => api.get('/certificates').then(r => r.data);
export const getCertAiAccuracy = (months = 1) => api.get(`/certificates/ai-accuracy?months=${months}`).then(r => r.data);
export const buildTrainingQueue = (photoIds, notes = '') =>
  api.post('/certificates/training/queue', { photoIds, notes }).then(r => r.data);
export const buildTrainingQueueByLayout = (photoIds, layoutId, notes = '') =>
  api.post('/certificates/training/queue-by-layout', { photoIds, layoutId, notes })
    .then(r => r.data);
export const listTrainingCards = (status = '') =>
  api.get(`/certificates/training/queue${status ? `?status=${status}` : ''}`).then(r => r.data);
export const submitTrainingCard = (cardId, values) =>
  api.post(`/certificates/training/cards/${cardId}/submit`, values).then(r => r.data);
export const skipTrainingCard = (cardId) =>
  api.post(`/certificates/training/cards/${cardId}/skip`).then(r => r.data);
export const deleteTrainingCard = (cardId) =>
  api.delete(`/certificates/training/cards/${cardId}`).then(r => r.data);
export const getTrainingStats = () => api.get('/certificates/training/stats').then(r => r.data);
export const listTrainingBoards = (queueId = '') =>
  api.get(`/certificates/training/boards${queueId ? `?queueId=${queueId}` : ''}`).then(r => r.data);
export const patchTrainingBoard = (boardId, payload) =>
  api.patch(`/certificates/training/boards/${boardId}`, payload).then(r => r.data);

export const deleteTrainingBoard = (boardId) =>
  api.delete(`/certificates/training/boards/${encodeURIComponent(boardId)}`).then(r => r.data);

export const deleteTrainingQueue = (queueId) =>
  api.delete(`/certificates/training/queue/${encodeURIComponent(queueId)}`).then(r => r.data);

// Cert rules engine
export const listCertRules = (scope = '') =>
  api.get(`/certificates/rules${scope ? `?scope=${scope}` : ''}`).then(r => r.data);
export const createCertRule = (payload) =>
  api.post('/certificates/rules', payload).then(r => r.data);
export const updateCertRule = (id, payload) =>
  api.put(`/certificates/rules/${id}`, payload).then(r => r.data);
export const deleteCertRule = (id) =>
  api.delete(`/certificates/rules/${id}`).then(r => r.data);
export const applyCertRules = (input, scope = 'card') =>
  api.post('/certificates/rules/apply', { input, scope }).then(r => r.data);
export const previewCertRules = (rules, sample) =>
  api.post('/certificates/rules/preview', { rules, sample }).then(r => r.data);
export const getCertRuleSuggestions = (minCount = 3) =>
  api.get(`/certificates/rules/suggestions?min_count=${minCount}`).then(r => r.data);

// Board layouts (preset distribution-board frame layouts for camera overlay)
export const listBoardLayouts = () =>
  api.get('/board-layouts').then(r => r.data);
export const getBoardLayout = (id) =>
  api.get(`/board-layouts/${id}`).then(r => r.data);
export const createBoardLayout = (payload) =>
  api.post('/board-layouts', payload).then(r => r.data);
export const updateBoardLayout = (id, payload) =>
  api.put(`/board-layouts/${id}`, payload).then(r => r.data);
export const deleteBoardLayout = (id) =>
  api.delete(`/board-layouts/${id}`).then(r => r.data);
export const duplicateBoardLayout = (id) =>
  api.post(`/board-layouts/${id}/duplicate`).then(r => r.data);
export const uploadBoardLayoutPhoto = (id, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post(`/board-layouts/${id}/photo`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};
export const deleteBoardLayoutPhoto = (id) =>
  api.delete(`/board-layouts/${id}/photo`).then(r => r.data);
export const snapBoardLayoutFrames = (id, payload = {}) =>
  api.post(`/board-layouts/${id}/snap-frames`, payload).then(r => r.data);
export const equaliseBoardLayoutFrames = (id, payload = {}) =>
  api.post(`/board-layouts/${id}/equalise-frames`, payload).then(r => r.data);
export const generateBoardLayoutFrames = (id, payload = {}) =>
  api.post(`/board-layouts/${id}/generate-frames-from-photo`, payload).then(r => r.data);
export const detectBoardLayoutPhases = (id, payload = {}) =>
  api.post(`/board-layouts/${id}/detect-phases`, payload).then(r => r.data);
export const boardLayoutPhotoUrl = (id) =>
  `${BASE}/board-layouts/${id}/photo`;

/**
 * Fetch the EXACT crop bytes the AI training queue + cert vision
 * pipeline would receive for a single frame. The caller is responsible
 * for `URL.revokeObjectURL(url)` on unmount to avoid leaking blobs.
 *
 * Authenticated streaming via `fetch` because the route requires a
 * Bearer token and `<img src>` can't carry that header.
 */
export const fetchBoardLayoutFrameCropBlobUrl = async (id, frameId, { padPct = 1.5 } = {}) => {
  const token = await ensureAccessToken();
  const url = `${BASE}/board-layouts/${id}/preview-crop?frameId=${encodeURIComponent(frameId)}&padPct=${padPct}`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    let detail = `Preview failed (HTTP ${res.status})`;
    try {
      const body = await res.json();
      if (body?.detail) detail = body.detail;
    } catch (_) { /* non-JSON error body */ }
    throw new Error(detail);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
};

/**
 * Fetch the layout's reference photo as an authenticated blob URL.
 * Used by the CertTrain frame-adjuster overlay so the engineer can
 * see the original photo + drag the bounding box on it.
 */
// (canonical definition lives further down — see line ~553)

/**
 * One-click "confirm / correct" path from the CertNew post-extraction
 * review screen. Engineer is on a circuit row, opens the crop popover,
 * and clicks either "✓ Confirm" or "🎯 Save correction" — we POST the
 * (layoutId, frameId, action, aiSnapshot, engineerValues) tuple and
 * the server bakes a `cert_training_cards` row with the crop attached.
 */
export const submitCertReviewCorrection = (payload) =>
  api.post('/certificates/training/queue/from-review', payload).then(r => r.data);

/**
 * Re-crop every pending training card in a queue using the current
 * cropping logic. Use this after fixing a coordinate-math bug in the
 * cropper — the crops persisted at queue-creation time are otherwise
 * stuck with the old coords forever.
 */
/**
 * Fetch the EXACT source photo a training card was built from
 * (`card.photoStoredAs`) as an authenticated blob URL. Used by the
 * CertTrain frame-adjuster so the engineer sees the same photo the
 * queue was created from — not the layout's reference photo, which
 * is often different or missing.
 *
 * Optional `signal` lets the caller abort a hung fetch via
 * `AbortController` — production ingresses sometimes wedge binary
 * streams and we don't want a 5G iPhone stuck spinning forever.
 */
export const fetchTrainingCardSourcePhotoBlobUrl = async (cardId, { signal } = {}) => {
  const token = await ensureAccessToken();
  const url = `${BASE}/certificates/training/cards/${encodeURIComponent(cardId)}/source-photo`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });
  if (!res.ok) {
    let detail = `Source photo fetch failed (HTTP ${res.status})`;
    try {
      const body = await res.json();
      if (body?.detail) detail = body.detail;
    } catch (_) { /* non-JSON error body */ }
    throw new Error(detail);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
};

export const recropPendingTrainingCards = (queueId) =>
  api.post(`/certificates/training/queue/${encodeURIComponent(queueId)}/recrop-pending`).then(r => r.data);

/**
 * Engineer-driven re-crop + re-extract for a single training card.
 * Persists the new x/y/w/h onto the layout's frame, re-cuts the crop,
 * and runs the zoom AI pass on the new bytes. Pending breaker /
 * isolator cards only.
 */
export const adjustTrainingCardFrameAndRerun = (cardId, coords) =>
  api.post(
    `/certificates/training/cards/${encodeURIComponent(cardId)}/adjust-frame-and-rerun`,
    coords,
  ).then(r => r.data);

// ---------- Reference Library ----------
export const listReferenceBrands = () =>
  api.get('/reference/brands').then(r => r.data.brands);

export const createReferenceBrand = (name) =>
  api.post('/reference/brands', { name }).then(r => r.data);

export const updateReferenceBrand = (id, payload) =>
  api.patch(`/reference/brands/${id}`, payload).then(r => r.data);

export const deleteReferenceBrand = (id) =>
  api.delete(`/reference/brands/${id}`).then(r => r.data);

export const listReferenceItems = (brandId, kind = null) => {
  const q = kind ? `?kind=${encodeURIComponent(kind)}` : '';
  return api.get(`/reference/brands/${brandId}/items${q}`).then(r => r.data.items);
};

export const createReferenceItem = (brandId, payload) =>
  api.post(`/reference/brands/${brandId}/items`, payload).then(r => r.data);

export const updateReferenceItem = (itemId, payload) =>
  api.patch(`/reference/items/${itemId}`, payload).then(r => r.data);

export const deleteReferenceItem = (itemId) =>
  api.delete(`/reference/items/${itemId}`).then(r => r.data);

export const uploadReferenceItemPhoto = (itemId, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post(`/reference/items/${itemId}/photo`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};

/**
 * Run the close-up AI vision pass on an arbitrary cropped image (no
 * item required). Used by the cropper's "Re-run AI" preview so the
 * engineer can see what the model reads off the latest crop bbox
 * before committing.
 */
export const aiExtractReferenceDevice = (file) => {
  const fd = new FormData();
  fd.append('photo', file);
  return api.post('/reference/items/ai-extract-device', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};

/**
 * Scan-previous-certificate flow — kicks off an async OCR job and
 * returns `{jobId, pageCount}`. The endpoint converts PDFs to images
 * server-side and runs each page through Claude in parallel.
 */
export const scanPreviousCertificate = (files) => {
  const fd = new FormData();
  for (const f of files) fd.append('pages', f);
  return api.post('/certificates/scan-previous', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  }).then(r => r.data);
};

/** Poll the status of a running scan job. */
export const getScanPreviousJob = (jobId) =>
  api.get(`/certificates/scan-previous/jobs/${jobId}`).then(r => r.data);

export const exportScanPreviousCsv = async (rows, filename) => {
  const res = await api.post(
    '/certificates/scan-previous/csv',
    { rows, filename },
    { responseType: 'blob' },
  );
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  // Pull filename from Content-Disposition if present.
  const cd = res.headers['content-disposition'] || '';
  const m = cd.match(/filename="([^"]+)"/);
  a.download = m ? m[1] : `${filename || 'lutrom-scan-previous'}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

export const referenceItemPhotoUrl = (itemId) =>
  `${BASE}/reference/items/${itemId}/photo`;

/**
 * Fetch a reference item's photo as a blob URL with auth header — for
 * use in `<img src>` since the route requires Bearer auth that an
 * unauthenticated img tag can't carry.
 */
export const fetchReferenceItemPhotoBlobUrl = async (itemId) => {
  const token = await ensureAccessToken();
  const res = await fetch(`${BASE}/reference/items/${itemId}/photo`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Photo fetch failed (HTTP ${res.status})`);
  return URL.createObjectURL(await res.blob());
};

/**
 * Promote a labelled training card into the reference library under
 * the chosen brand. Lifts the card's submission as the item's tags
 * and copies its crop bytes as the item's photo (no double-entry).
 */
export const promoteTrainingCardToLibrary = (cardId, payload) =>
  api.post(`/reference/items/from-training-card/${encodeURIComponent(cardId)}`, payload).then(r => r.data);

/**
 * Fetch a board-layout reference photo as an authenticated blob and
 * return an object URL. <img src="..."> can't send the Bearer-token
 * header so we have to mediate the download ourselves.
 *
 * The caller is responsible for `URL.revokeObjectURL(url)` on unmount
 * to avoid leaking blobs.
 */
export const fetchBoardLayoutPhotoBlobUrl = async (id) => {
  const token = await ensureAccessToken();
  const res = await fetch(`${BASE}/board-layouts/${id}/photo`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Photo fetch failed: ${res.status}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
};
export const getCertificate = (id) => api.get(`/certificates/${id}`).then(r => r.data);
export const issueCertificate = (id) => api.post(`/certificates/${id}/issue`).then(r => r.data);
export const certPdfUrl = (id) => `${BASE}/certificates/${id}/pdf`;
export const certCsvUrl = (id) => `${BASE}/certificates/${id}/export.csv`;
export const certJsonUrl = (id) => `${BASE}/certificates/${id}/export.json`;

// Generic upload helper used for cert photo staging (returns {storedAs, url})
export const uploadStaging = async (file) => {
  // Re-uses the work-order attachment endpoint's storage backend by piping through
  // a "staging" service request. For simplicity, use a per-session helper:
  // we'll attach via a direct multipart POST to a new staging endpoint.
  const fd = new FormData();
  fd.append('file', file);
  return api.post('/certificates/stage-upload', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};

// Invoices
export const getInvoices = () => api.get('/invoices').then(r => r.data);
export const getInvoice = (id) => api.get(`/invoices/${id}`).then(r => r.data);
export const updateInvoice = (id, body) => api.patch(`/invoices/${id}`, body).then(r => r.data);
export const updateInvoiceIssueDate = (id, issuedAt) =>
  api.post(`/invoices/${id}/issue-date`, { issuedAt }).then(r => r.data);
export const renameInvoice = (id, newId) => api.post(`/invoices/${id}/rename`, { newId }).then(r => r.data);
export const applyDiscountsToInvoice = (id, creditIds) =>
  api.post(`/invoices/${id}/apply-discounts`, { creditIds }).then(r => r.data);
export const unapplyDiscountFromInvoice = (id, creditId) =>
  api.post(`/invoices/${id}/unapply-discount/${creditId}`).then(r => r.data);
export const discardPendingDiscountCredit = (creditId) =>
  api.delete(`/discount-credits/${creditId}`).then(r => r.data);
export const rebuildInvoiceFromSource = (id) =>
  api.post(`/invoices/${id}/rebuild-from-source`).then(r => r.data);
// Toggle customer-portal visibility. Works on ANY invoice status
// (draft, sent, pushed, paid) — the draft-only lock on
// `updateInvoice` doesn't apply here since we're not editing the
// invoice content, just its portal visibility flag.
export const setInvoiceVisibility = (id, hiddenFromCustomer) =>
  api.post(`/invoices/${id}/visibility`, { hiddenFromCustomer }).then(r => r.data);
// Admin-triggered payment thank-you email (auto-send was removed —
// admins now must click "Send thank-you" from the invoice page).
export const sendPaymentThankYou = (invoiceId) =>
  api.post(`/invoices/${invoiceId}/send-thank-you`).then(r => r.data);


// ---- EICR Section K observation reports ----
export const listEicrReports = () => api.get('/eicr-reports').then(r => r.data);
export const getEicrReport = (id) => api.get(`/eicr-reports/${id}`).then(r => r.data);
export const createEicrReport = (body) => api.post('/eicr-reports', body).then(r => r.data);
// Strip inline base64 photo bytes from the outgoing PATCH — they can
// balloon the payload past 10 MB on reports with a handful of iPhone
// photos, which stalls the request and eventually errors as "Load
// failed" in Safari. Photos are managed by the dedicated upload
// endpoints; the backend merges them back in from the existing doc.
const _stripEicrPhotoBytes = (body) => {
  if (!body || typeof body !== 'object') return body;
  const out = { ...body };
  if ('coverPhoto' in out) delete out.coverPhoto;
  if ('coverLogo' in out) delete out.coverLogo;
  if (Array.isArray(out.findings)) {
    out.findings = out.findings.map((f) => ({
      ...f,
      photos: [],   // empty → backend keeps existing photos for this index
    }));
  }
  return out;
};
export const updateEicrReport = (id, body) =>
  // slim=1 → backend strips photo bytes from the response. Photos
  // never actually change via PATCH (they're managed by the dedicated
  // /photos endpoint), so the client keeps its existing photo state.
  api.patch(`/eicr-reports/${id}?slim=1`, _stripEicrPhotoBytes(body)).then((r) => r.data);
export const deleteEicrReport = (id) => api.delete(`/eicr-reports/${id}`).then(r => r.data);
export const duplicateEicrReport = (id) => api.post(`/eicr-reports/${id}/duplicate`).then(r => r.data);
export const emailEicrReport = (id, body) => api.post(`/eicr-reports/${id}/email`, body).then(r => r.data);
export const previewEicrReportEmail = (id, body) =>
  api.post(`/eicr-reports/${id}/email-preview`, body).then((r) => r.data);
// Autocomplete across every customer's primary + sub-contacts. Used by
// the EICR email dialog so the engineer can send a report straight to
// "Mike at Cammell Laird" even when the customer we're billing is
// "Dave Tinsley Consultancy".
export const searchCustomerContacts = (q, limit = 20) =>
  api.get('/customers/contacts/search', { params: { q, limit } }).then((r) => r.data);
// Manually create an empty draft invoice (from the "New invoice" button
// on the Invoices list). Server returns the full invoice doc so the UI
// can redirect straight to the detail page.
export const createManualDraftInvoice = (payload) =>
  api.post('/invoices/draft', payload).then((r) => r.data);
// Legacy signature (URL only) — replaced by the blob-based helper below
// so the token isn't lost when the browser navigates the URL directly.
// Kept temporarily for any lingering callers.
export const fetchEicrReportPdfUrl = (id) => `${BASE}/eicr-reports/${id}/pdf`;
// Returns { url, filename } for preview (iframe) or download (anchor).
// Uses the standard `_fetchPdfAsBlobUrl` helper so JWT auth is sent as
// a Bearer header rather than a broken `?t=` query-param hack.
export const fetchEicrReportPdfBlobUrl = (id) =>
  _fetchPdfAsBlobUrl(`/eicr-reports/${id}/pdf`);
export const uploadEicrPhoto = (id, kind, findingIndex, file) => {
  const form = new FormData();
  form.append('kind', kind);
  form.append('findingIndex', String(findingIndex));
  form.append('file', file);
  return api.post(`/eicr-reports/${id}/photos`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};
export const deleteEicrPhoto = (id, { kind, findingIndex = -1, photoIndex = -1 }) =>
  api.delete(`/eicr-reports/${id}/photos`, {
    params: { kind, findingIndex, photoIndex },
  }).then(r => r.data);
export const getEicrPresets = () => api.get('/eicr-reason-presets').then(r => r.data);
export const saveEicrPresets = (presets) => api.put('/eicr-reason-presets', presets).then(r => r.data);

// ---- Address lookup (Nominatim/OSM proxy) ----
export const getAddressSettings = () => api.get('/address/settings').then(r => r.data);
export const saveAddressKey = (apiKey) => api.put('/address/settings', { apiKey }).then(r => r.data);
export const deleteAddressKey = () => api.delete('/address/settings').then(r => r.data);
export const postcodePremises = (pc) => api.get(`/address/postcode/${encodeURIComponent(pc)}`).then(r => r.data);
export const retryXeroAddressSync = (kind, id) => api.post(`/xero/address-sync/${kind}/${id}/retry`).then(r => r.data);
export const addressLookup = (q) => api.get('/address/lookup', { params: { q } }).then(r => r.data);
export const authoriseInvoice = (id) => api.post(`/xero/invoices/${id}/authorise`).then(r => r.data);
export const getCustomerPaymentTerms = (customerId) =>
  api.get(`/customers/${customerId}/payment-terms`).then(r => r.data);
export const updateCustomerPaymentTerms = (customerId, body) =>
  api.patch(`/customers/${customerId}/payment-terms`, body).then(r => r.data);
export const deleteInvoice = (id, { voidXero = false } = {}) =>
  api.delete(`/invoices/${id}${voidXero ? '?void_xero=true' : ''}`).then(r => r.data);
export const detachWorkOrderFromInvoice = (invoiceId, woId) =>
  api.post(`/invoices/${invoiceId}/detach-work-order/${woId}`).then(r => r.data);
// Pull just one invoice's latest state from Xero (line items, totals,
// dates, status). Used by the "Refresh from Xero" button on
// AdminInvoiceDetail so admin edits in Xero reflect in the portal
// without waiting for the periodic full sync.
export const refreshInvoiceFromXero = (invoiceId) =>
  api.post(`/xero/invoices/${invoiceId}/refresh-from-xero`).then(r => r.data);

// Orphan invoice cleanup — clears the WO's invoice foreign key without
// touching the (already-deleted) invoice. Used from the labour panel's
// rose orphan banner.
export const detachOrphanInvoice = (woId) =>
  api.post(`/work-orders/${woId}/detach-invoice`).then(r => r.data);

// Re-attach this WO to a different local invoice — the typical case
// is "the original invoice was renamed in Xero and re-imported under
// a new local id".
export const reattachWorkOrderToInvoice = (woId, newInvoiceId) =>
  api.post(`/work-orders/${woId}/reattach-invoice/${newInvoiceId}`).then(r => r.data);
export const getCustomerOverdueInvoices = (id) => api.get(`/customers/${id}/overdue-invoices`).then(r => r.data);
// Send an overdue-invoice chase.
//   `force`       – bypass the 72h re-send throttle.
//   `to`          – override the primary recipient (defaults to customer.email).
//   `cc`          – override the auto-generated cc list (siblings). Empty
//                    array means "no cc"; omit entirely to keep the
//                    default sibling-cc behaviour.
//   `saveDefault` – persist `to` as the customer's primary email so the
//                    next chase / invoice email uses it too.
export const chaseCustomerOverdue = (id, { force = false, to, cc, saveDefault, emails } = {}) => {
  const params = force ? '?force=true' : '';
  const body = {};
  if (to !== undefined) body.to = to;
  if (cc !== undefined) body.cc = cc;
  if (saveDefault !== undefined) body.saveDefault = saveDefault;
  if (emails && Object.keys(emails).length) body.emails = emails;
  return api.post(`/customers/${id}/chase-overdue${params}`, body).then(r => r.data);
};
export const mergeCustomers = (winnerId, loserId, { deleteLoser = true } = {}) =>
  api.post(`/customers/${winnerId}/merge/${loserId}?delete_loser=${deleteLoser ? 'true' : 'false'}`).then(r => r.data);

// Schedule / time
export const getSchedule = (params = {}) => api.get('/schedule', { params }).then(r => r.data);
export const createShift = (data) => api.post('/schedule', data).then(r => r.data);
export const updateShift = (id, data) => api.patch(`/schedule/${id}`, data).then(r => r.data);
export const deleteShift = (id) => api.delete(`/schedule/${id}`).then(r => r.data);

// Site-performance analytics (admin Overview + admin CustomerDetail card + customer Overview).
// Passing `customerId` narrows the whole payload to that customer + its
// sub-customers — powers the "How are we doing at <customer>'s sites?"
// block on the admin detail page.
export const getSitePerformance = (period = 'month', customerId = '') =>
  api.get('/analytics/site-performance', {
    params: customerId ? { period, customerId } : { period },
  }).then(r => r.data);

// Admin Reports (unified certs + EICR + uploaded PDFs)
export const getAdminReports = () => api.get('/admin/reports').then(r => r.data);
export const getAdminReportTypes = () => api.get('/admin/reports/types').then(r => r.data.types);
export const uploadAdminReport = (formData) =>
  api.post('/admin/reports/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
export const deleteAdminReport = (id) => api.delete(`/admin/reports/${id}`).then(r => r.data);
export const adminReportFileUrl = (id) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/admin/reports/${id}/file`;

// Google Calendar pairing (per-user)
export const getCalendarStatus = () => api.get('/calendar/status').then(r => r.data);
export const startCalendarOAuth = () => api.get('/oauth/calendar/login').then(r => r.data);
export const disconnectCalendar = () => api.post('/calendar/disconnect').then(r => r.data);
export const getCalendarEvents = (from, to) =>
  api.get('/calendar/events', { params: { from, to } }).then(r => r.data);
export const getTimeEntries = () => api.get('/time-entries').then(r => r.data);
export const createTimeEntry = (data) => api.post('/time-entries', data).then(r => r.data);
export const getTodayTimeSummary = (date) =>
  api.get('/time-entries/today-summary', { params: date ? { date } : {} }).then(r => r.data);
export const getActiveTimeEntry = () => api.get('/time-entries/active').then(r => r.data);
export const clockIn = (data) => api.post('/time-entries/clock-in', data).then(r => r.data);
export const clockOut = (data) => api.post('/time-entries/clock-out', data).then(r => r.data);
export const discardTimeEntry = (id) => api.delete(`/time-entries/${id}`).then(r => r.data);
export const forgotClockOut = (id, body) => api.post(`/time-entries/${id}/forgot-clockout`, body).then(r => r.data);
export const getUserTimesheet = (userId, days = 30) =>
  api.get(`/users/${userId}/timesheet`, { params: { days } }).then(r => r.data);

// Chat
export const getChannels = () => api.get('/chat/channels').then(r => r.data);
export const getMessages = (channelId) => api.get(`/chat/channels/${channelId}/messages`).then(r => r.data);
export const sendMessage = (channelId, text) => api.post(`/chat/channels/${channelId}/messages`, { text }).then(r => r.data);

// Xero integration
export const getXeroStatus = () => api.get('/xero/status').then(r => r.data);
export const getXeroConnectUrl = () => api.get('/xero/connect').then(r => r.data);
export const disconnectXero = () => api.post('/xero/disconnect').then(r => r.data);
export const searchXeroContacts = (q) => api.get('/xero/contacts/search', { params: { q } }).then(r => r.data);
export const getAllXeroContacts = () => api.get('/xero/contacts/all').then(r => r.data);
export const mapXeroContact = (customerId, xeroContactId) => api.post(`/xero/contacts/map/${customerId}`, { xeroContactId }).then(r => r.data);
export const pushInvoiceToXero = (id) => api.post(`/xero/invoices/${id}/push`).then(r => r.data);
export const pullInvoicesFromXero = () => api.post('/xero/invoices/sync-from-xero').then(r => r.data);

// Printable barcode labels (Code 128)
export const openPartLabelPdf = async (partId, copies = 1) => {
  const token = await ensureAccessToken();
  const res = await fetch(`${BASE}/inventory/parts/${partId}/label.pdf?copies=${copies}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to generate label');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

export const openBulkLabelsPdf = async (partIds) => {
  const token = await ensureAccessToken();
  const res = await fetch(`${BASE}/inventory/labels.pdf?ids=${partIds.join(',')}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to generate labels');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

// Auto-replenish PDF — purchase order for low-stock parts
export const openReplenishPdf = async (supplier) => {
  const token = await ensureAccessToken();
  const url = supplier
    ? `${BASE}/inventory/replenish.pdf?supplier=${encodeURIComponent(supplier)}`
    : `${BASE}/inventory/replenish.pdf`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new Error(detail?.detail || 'Failed to generate replenishment order');
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  window.open(objectUrl, '_blank');
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
};

export const getReplenishSuppliers = () => api.get('/inventory/replenish/suppliers').then(r => r.data);
export const getEmailStatus = () => api.get('/email/status').then(r => r.data);
export const emailReplenishPo = (payload) => api.post('/inventory/replenish/email', payload).then(r => r.data);
// Test send — routes the email to the calling admin's own inbox and
// makes zero DB writes / no customer notification. Handy for previewing
// exactly what the customer will see before firing the real one.
export const sendTestQuoteEmail = (id, payload = {}) =>
  api.post(`/quotes/${id}/email`, { ...payload, test_to_admin: true }).then(r => r.data);
export const emailQuote = (quoteId, payload) => api.post(`/quotes/${quoteId}/email`, payload).then(r => r.data);
// Reminder is the same endpoint with a flag — subject gets a "Reminder — "
// prefix and the body is rewritten as a gentle nudge.
export const remindQuote = (id, body = {}) =>
  api.post(`/quotes/${id}/email`, { ...body, reminder: true }).then(r => r.data);
// Ask Claude to draft priced line items from the linked service
// request (or the quote's own title/description as a fallback).
export const draftQuoteLineItemsWithAI = (id) =>
  api.post(`/quotes/${id}/ai-line-items`).then(r => r.data);
export const emailInvoice = (invoiceId, payload) => api.post(`/invoices/${invoiceId}/email`, payload).then(r => r.data);
export const previewInvoiceEmail = (invoiceId, payload) => api.post(`/invoices/${invoiceId}/email-preview`, payload).then(r => r.data);
// Same shape as the invoice preview — returns rendered subject/html
// and defaults for every editable field so the quote email dialog
// can mirror the invoice preview flow exactly.
export const previewQuoteEmail = (quoteId, payload) =>
  api.post(`/quotes/${quoteId}/email-preview`, payload).then(r => r.data);
export const getMonthlyMargin = (months = 6) => api.get(`/work-orders/margin/monthly?months=${months}`).then(r => r.data);

// Xero → Inventory (drafts workflow)
export const syncXeroItems = () => api.post('/xero/inventory/sync-from-xero').then(r => r.data);
export const finalizeDraftPart = (partId, data) => api.post(`/xero/inventory/${partId}/finalize`, data).then(r => r.data);

// Work order labor + invoice accumulation
export const editLaborOnWorkOrder = (woId, index, patch) =>
  api.patch(`/xero/work-orders/${woId}/labor/${index}`, patch).then(r => r.data);
export const addLaborToWorkOrder = (woId, data) => api.post(`/xero/work-orders/${woId}/labor`, data).then(r => r.data);
export const removeLaborFromWorkOrder = (woId, index) => api.delete(`/xero/work-orders/${woId}/labor/${index}`).then(r => r.data);
export const getActiveTimer = (woId) => api.get(`/xero/work-orders/${woId}/timer`).then(r => r.data);
export const startLaborTimer = (woId, data) => api.post(`/xero/work-orders/${woId}/timer/start`, data).then(r => r.data);
export const stopLaborTimer = (woId) => api.post(`/xero/work-orders/${woId}/timer/stop`).then(r => r.data);
export const cancelLaborTimer = (woId) => api.delete(`/xero/work-orders/${woId}/timer`).then(r => r.data);
export const invoiceWorkOrder = (woId) => api.post(`/xero/work-orders/${woId}/invoice`).then(r => r.data);
export const approveWorkOrder = (woId) => api.post(`/work-orders/${woId}/approve`).then(r => r.data);
export const rejectWorkOrder = (woId, notes) =>
  api.post(`/work-orders/${woId}/reject`, { notes }).then(r => r.data);
export const getRamsSigners = (woId, doc) => api.get(`/work-orders/${woId}/rams/${doc}/signers`).then(r => r.data);
export const previewRamsEmail = (woId, doc, body) => api.post(`/work-orders/${woId}/rams/${doc}/email-preview`, body).then(r => r.data);
export const emailRams = (woId, doc, body) => api.post(`/work-orders/${woId}/rams/${doc}/email`, body).then(r => r.data);

export const acknowledgeRams = (woId, ramsId, signature) =>
  api.post(`/work-orders/${woId}/rams/acknowledge`, { ramsId, signature }).then(r => r.data);
export const resolvePendingLabourRate = (woId, index, data) =>
  api.post(`/work-orders/${woId}/labor/${index}/resolve-rate`, data).then(r => r.data);
export const markInvoiceSent = (invoiceId, pushStatus = true) => api.post(`/xero/invoices/${invoiceId}/mark-sent`, { pushStatus }).then(r => r.data);

// Invite acceptance (token-only, no auth required)
export const lookupInvite = (token) => api.get(`/auth/invites/${token}`).then(r => r.data);
export const redeemInvite = (token, password) =>
  api.post(`/auth/invites/${token}/redeem`, { password }).then(r => r.data);
export const resendInvite = (userId) =>
  api.post(`/users/${userId}/resend-invite`).then(r => r.data);
export const getRateCards = () => api.get('/rate-cards').then(r => r.data);
export const createRateCard = (data) => api.post('/rate-cards', data).then(r => r.data);
export const updateRateCard = (id, data) => api.patch(`/rate-cards/${id}`, data).then(r => r.data);
export const deleteRateCard = (id) => api.delete(`/rate-cards/${id}`).then(r => r.data);
export const seedStandardRates = (data) => api.post('/rate-cards/seed-standard', data).then(r => r.data);
export const getCustomerRateCardSummary = (customerId) =>
  api.get(`/customers/${customerId}/rate-card-summary`).then(r => r.data);
export const testRateResolve = (data) => api.post('/rate-cards/resolve', data).then(r => r.data);
export const getBankHolidays = () => api.get('/rate-cards/bank-holidays').then(r => r.data);
export const setBankHolidays = (dates) => api.put('/rate-cards/bank-holidays', { dates }).then(r => r.data);

// Quotes
export const getQuotes = () => api.get('/xero/quotes').then(r => r.data);
export const getQuote = (id) => api.get(`/xero/quotes/${id}`).then(r => r.data);
export const createQuote = (data) => api.post('/xero/quotes', data).then(r => r.data);
export const updateQuote = (id, data) => api.patch(`/xero/quotes/${id}`, data).then(r => r.data);
export const deleteQuote = (id) => api.delete(`/xero/quotes/${id}`).then(r => r.data);
export const pushQuoteToXero = (id) => api.post(`/xero/quotes/${id}/push-to-xero`).then(r => r.data);
export const convertQuoteToWorkOrder = (id) => api.post(`/xero/quotes/${id}/convert`).then(r => r.data);

// Customer-facing quote actions
export const customerAcceptQuote = (id, note = '') => api.post(`/xero/quotes/${id}/customer-accept`, { note }).then(r => r.data);
export const customerDeclineQuote = (id, note = '') => api.post(`/xero/quotes/${id}/customer-decline`, { note }).then(r => r.data);

// Bills inbox + Gmail OAuth
export const getGmailStatus = () => api.get('/gmail/status').then(r => r.data);
export const getGmailLoginUrl = () => api.post('/oauth/gmail/login').then(r => r.data);
export const disconnectGmail = () => api.post('/gmail/disconnect').then(r => r.data);
export const getBills = (status) => api.get('/bills', { params: status ? { status } : {} }).then(r => r.data);
export const getBillStats = () => api.get('/bills/stats').then(r => r.data);
export const getBill = (id) => api.get(`/bills/${id}`).then(r => r.data);
export const updateBill = (id, patch) => api.patch(`/bills/${id}`, patch).then(r => r.data);
export const dismissBill = (id) => api.post(`/bills/${id}/dismiss`).then(r => r.data);
export const pushBillToXero = (id) => api.post(`/bills/${id}/push-xero`).then(r => r.data);
export const reattachBillToXero = (id) => api.post(`/bills/${id}/reattach-to-xero`).then(r => r.data);
export const replaceBillReceipt = (id, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post(`/bills/${id}/replace-receipt`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};
export const getUnattachedBills = () => api.get('/bills/unattached').then(r => r.data);

/** Light progress probe used by the WO RAMS regenerate UI while the
 * synchronous `/rams/generate` POST is still in flight (30-60 s). */
export const getRamsProgress = (workOrderId) =>
  api.get(`/work-orders/${workOrderId}/rams/progress`).then(r => r.data);
export const getReadyToPushBills = () => api.get('/bills/ready-to-push').then(r => r.data);
export const pushAllGreenBills = () => api.post('/bills/push-all-green').then(r => r.data);
export const getBillsSettings = () => api.get('/bills/settings').then(r => r.data);
export const updateBillsSettings = (patch) => api.patch('/bills/settings', patch).then(r => r.data);
export const sendBillsDigestNow = () => api.post('/bills/digest-now').then(r => r.data);
export const pollBillsNow = () => api.post('/bills/poll-now').then(r => r.data);
export const learnFromHistory = () => api.post('/bills/learn-from-history').then(r => r.data);
export const deleteBill = (id) => api.delete(`/bills/${id}`).then(r => r.data);
export const getBillAttachment = (id, filename) =>
  api.get(`/bills/${id}/attachment`, {
    params: filename ? { name: filename } : {},
    responseType: 'blob',
  }).then(r => r.data);
export const verifyBillLine = (billId, lineIdx, line) =>
  api.post(`/bills/${billId}/lines/${lineIdx}/verify`, line || null).then(r => r.data);
export const verifyBillAllLines = (billId) =>
  api.post(`/bills/${billId}/verify-all`).then(r => r.data);

// Xero chart of accounts (for the Bills review screen line dropdowns)
export const getXeroAccounts = () => api.get('/xero/accounts').then(r => r.data);

// Suppliers — all the trade accounts we BUY from (Toolstation, Wickes, CEF, …)
export const getSuppliers = () => api.get('/suppliers').then(r => r.data);
export const bulkDeleteSuppliers = (ids) =>
  api.post('/suppliers/bulk-delete', { ids }).then(r => r.data);
export const findDuplicateSuppliers = () =>
  api.get('/supplier-duplicates').then(r => r.data);
export const mergeSuppliers = (keeperId, loserIds) =>
  api.post('/supplier-merge', { keeperId, loserIds }).then(r => r.data);
export const getSupplier = (id) => api.get(`/suppliers/${id}`).then(r => r.data);
export const getSupplierCategories = () => api.get('/suppliers/categories').then(r => r.data);
export const createSupplier = (data) => api.post('/suppliers', data).then(r => r.data);
export const updateSupplier = (id, patch) => api.patch(`/suppliers/${id}`, patch).then(r => r.data);
export const deleteSupplier = (id) => api.delete(`/suppliers/${id}`).then(r => r.data);
export const syncSuppliersFromXero = () => api.post('/suppliers/sync-from-xero').then(r => r.data);
export const backfillSuppliersFromBills = () => api.post('/suppliers/backfill-from-bills').then(r => r.data);
export const saveSupplierMemberCard = (id, data) =>
  api.post(`/suppliers/${id}/member-card`, data).then(r => r.data);
export const deleteSupplierMemberCard = (id) =>
  api.delete(`/suppliers/${id}/member-card`).then(r => r.data);
export const uploadSupplierCardPhoto = (id, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post(`/suppliers/${id}/member-card-photo`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};

// Inventory categories (for markup-based sell-price calculation)
export const getCategories = () => api.get('/inventory/categories').then(r => r.data);
export const createCategory = (data) => api.post('/inventory/categories', data).then(r => r.data);
export const updateCategory = (id, data) => api.patch(`/inventory/categories/${id}`, data).then(r => r.data);
export const deleteCategory = (id) => api.delete(`/inventory/categories/${id}`).then(r => r.data);

// Draft inventory parts (created automatically from DIRECTCOSTS bill lines)
export const getDraftParts = () => api.get('/inventory/draft-parts').then(r => r.data);
export const updateDraftPart = (id, data) => api.patch(`/inventory/draft-parts/${id}`, data).then(r => r.data);
export const approveDraftPart = (id, payload = {}) => api.post(`/inventory/draft-parts/${id}/approve`, payload).then(r => r.data);
export const discardDraftPart = (id) => api.delete(`/inventory/draft-parts/${id}`).then(r => r.data);

// Status / priority labels (kept frontend-only)
export const STATUS_LABELS = {
  open: { label: 'Open', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800 border-green-200' },
  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-800 border-red-200' },
  pending: { label: 'Pending', color: 'bg-slate-100 text-slate-800 border-slate-200' },
  scheduled: { label: 'Scheduled', color: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  // Backlog bucket — WO exists but hasn't been given a date yet. Slate
  // so it visually reads as "on hold" without the alarm of red/amber.
  unscheduled: { label: 'Unscheduled', color: 'bg-slate-200 text-slate-800 border-slate-300' },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-800 border-green-200' },
  operational: { label: 'Operational', color: 'bg-green-100 text-green-800 border-green-200' },
  maintenance_due: { label: 'Maintenance Due', color: 'bg-blue-100 text-blue-800 border-blue-200' },
};

export const PRIORITY_LABELS = {
  high: { label: 'High', color: 'text-red-600 bg-red-50 border-red-200' },
  medium: { label: 'Medium', color: 'text-blue-700 bg-blue-50 border-blue-200' },
  low: { label: 'Low', color: 'text-slate-700 bg-slate-50 border-slate-200' },
};

export default api;

// ---- PDF templates ----
export const getTemplateRegistry = () =>
  api.get('/pdf-templates/registry').then(r => r.data);
export const listTemplates = (kind) =>
  api.get('/pdf-templates', { params: kind ? { kind } : {} }).then(r => r.data);
export const getActiveTemplate = (kind) =>
  api.get(`/pdf-templates/active/${kind}`).then(r => r.data);
export const getTemplate = (id) =>
  api.get(`/pdf-templates/${id}`).then(r => r.data);
export const createTemplate = (body) =>
  api.post('/pdf-templates', body).then(r => r.data);
export const updateTemplate = (id, body) =>
  api.patch(`/pdf-templates/${id}`, body).then(r => r.data);
export const deleteTemplate = (id) =>
  api.delete(`/pdf-templates/${id}`).then(r => r.data);
export const duplicateTemplate = (id, { kind, name }) =>
  api.post(`/pdf-templates/${id}/duplicate`, { kind, name }).then(r => r.data);
export const activateTemplate = (id) =>
  api.post(`/pdf-templates/${id}/activate`).then(r => r.data);
export const deactivateTemplate = (id) =>
  api.post(`/pdf-templates/${id}/deactivate`).then(r => r.data);
export const uploadTemplateLetterhead = (id, file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post(`/pdf-templates/${id}/letterhead`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};
export const deleteTemplateLetterhead = (id) =>
  api.delete(`/pdf-templates/${id}/letterhead`).then(r => r.data);
export const fetchTemplatePreviewUrl = (id) =>
  _fetchPdfAsBlobUrl(`/pdf-templates/${id}/preview`);

// Authenticated blob URL for the rasterised letterhead background. The
// editor's `<img src>` swap (replacing the iframe) needs the token in
// the request and `<img>` can't send headers — so we round-trip via
// fetch + Object URL like the PDF preview does.
export const fetchLetterheadImageUrl = async (templateId, isLetterhead = false) => {
  const path = isLetterhead
    ? `/pdf-letterheads/${templateId}/letterhead-image`
    : `/pdf-templates/${templateId}/letterhead-image`;
  const token = await ensureAccessToken();
  const res = await fetch(`${BASE}${path}?_t=${Date.now()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return null;   // 404 when no letterhead — caller handles
  const blob = await res.blob();
  return URL.createObjectURL(blob);
};

// ---- Shared letterheads (`pdf_letterheads`) ----
// Reusable branding layer; multiple templates can share one letterhead
// PDF + placeholder set via `letterheadId`.
export const listLetterheads = () =>
  api.get('/pdf-letterheads').then(r => r.data);
export const getLetterhead = (id) =>
  api.get(`/pdf-letterheads/${id}`).then(r => r.data);
export const createLetterhead = (body) =>
  api.post('/pdf-letterheads', body).then(r => r.data);
export const updateLetterhead = (id, body) =>
  api.patch(`/pdf-letterheads/${id}`, body).then(r => r.data);
export const deleteLetterhead = (id) =>
  api.delete(`/pdf-letterheads/${id}`).then(r => r.data);
export const uploadLetterheadFile = (id, file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post(`/pdf-letterheads/${id}/file`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};
export const deleteLetterheadFile = (id) =>
  api.delete(`/pdf-letterheads/${id}/file`).then(r => r.data);
export const shareLetterhead = (id, { templateIds, detach = false }) =>
  api.post(`/pdf-letterheads/${id}/share`, { templateIds, detach }).then(r => r.data);
export const fetchLetterheadPreviewUrl = (id) =>
  _fetchPdfAsBlobUrl(`/pdf-letterheads/${id}/preview`);

// ---- Taxonomy (work-order categories, asset types) ----
export const listTaxonomy = (kind) =>
  api.get(`/taxonomy/${kind}`).then(r => r.data);
export const createTaxonomyOption = (kind, label, parentId = null) =>
  api.post('/taxonomy', { kind, label, parentId }).then(r => r.data);
export const updateTaxonomyOption = (id, patch) =>
  api.patch(`/taxonomy/${id}`, patch).then(r => r.data);
export const deleteTaxonomyOption = (id) =>
  api.delete(`/taxonomy/${id}`).then(r => r.data);

// ---- User preferences (sticky list sort, future UI prefs) ----
// Server-backed so the same admin gets the same sort on phone + laptop.
// We cache the bag in localStorage so first paint after navigation is
// instant — server is then used as source of truth on the next refresh.
const _PREFS_CACHE_KEY = 'lutrom.userPrefs.cache';
let _prefsCache = null;
let _prefsLoaded = false;
const _prefsListeners = new Set();

const _readCachedPrefs = () => {
  try {
    const raw = localStorage.getItem(_PREFS_CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
};
const _writeCachedPrefs = (bag) => {
  try { localStorage.setItem(_PREFS_CACHE_KEY, JSON.stringify(bag)); } catch { /* noop */ }
};

export const fetchUserPreferences = async () => {
  try {
    const bag = await api.get('/me/preferences').then(r => r.data);
    _prefsCache = bag || {};
    _prefsLoaded = true;
    _writeCachedPrefs(_prefsCache);
    // Notify any mounted SortBy / pref-aware components so they
    // re-hydrate without waiting for a navigation.
    _prefsListeners.forEach((fn) => { try { fn(_prefsCache); } catch { /* noop */ } });
    return _prefsCache;
  } catch {
    return _readCachedPrefs();
  }
};

export const onPreferencesUpdated = (fn) => {
  _prefsListeners.add(fn);
  return () => _prefsListeners.delete(fn);
};

export const getCachedPreference = (key, fallback = null) => {
  if (!_prefsCache) _prefsCache = _readCachedPrefs();
  return _prefsCache[key] ?? fallback;
};

export const isPrefsLoaded = () => _prefsLoaded;

export const setUserPreference = async (key, value) => {
  if (!_prefsCache) _prefsCache = _readCachedPrefs();
  _prefsCache[key] = value;
  _writeCachedPrefs(_prefsCache);
  try {
    await api.put('/me/preferences', { key, value });
  } catch { /* offline write — local cache will sync on next online write */ }
};



// ─────────────── Marketing Hub (Insights authoring) ───────────────
// See `/app/memory/insights-authoring-handoff.md` for the contract
// with the marketing repo. The portal outputs a ZIP of 5 files that
// go into the Next.js repo behind www.lutrom.co.uk.
export const getMarketingReference = () =>
  api.get('/marketing/reference').then(r => r.data);
export const listMarketingArticles = () =>
  api.get('/marketing/articles').then(r => r.data);
export const getMarketingArticle = (id) =>
  api.get(`/marketing/articles/${id}`).then(r => r.data);
export const createMarketingArticle = (data) =>
  api.post('/marketing/articles', data).then(r => r.data);
export const updateMarketingArticle = (id, data) =>
  api.patch(`/marketing/articles/${id}`, data).then(r => r.data);
export const deleteMarketingArticle = (id) =>
  api.delete(`/marketing/articles/${id}`).then(r => r.data);
export const publishMarketingArticle = (id) =>
  api.post(`/marketing/articles/${id}/publish`).then(r => r.data);
export const uploadMarketingHero = (id, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post(`/marketing/articles/${id}/hero`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};
// URLs for hero, OG image, LinkedIn PDF, and the artefacts ZIP —
// exposed as async blob fetchers so the auth JWT can be attached
// (streaming endpoints reject anonymous requests). Callers turn the
// blob into an object URL for `<img src>` / `<a href>`.
const _blobFetch = (path) =>
  api.get(path, { responseType: 'blob' }).then(r => r.data);
export const fetchMarketingHeroBlob = (id) =>
  _blobFetch(`/marketing/articles/${id}/hero`);
export const fetchMarketingOgBlob = (id) =>
  _blobFetch(`/marketing/articles/${id}/og`);
export const fetchMarketingLinkedInPdfBlob = (id) =>
  _blobFetch(`/marketing/articles/${id}/linkedin-pdf`);
export const previewMarketingLinkedIn = (id, linkedinCarousel) =>
  api.post(`/marketing/articles/${id}/linkedin-preview`, { linkedinCarousel: linkedinCarousel || null }).then(r => r.data);
export const postMarketingLinkedInNow = (id, body) =>
  api.post(`/marketing/articles/${id}/linkedin/post-now`, body).then(r => r.data);
export const fetchMarketingArtefactsZipBlob = (id) =>
  _blobFetch(`/marketing/articles/${id}/artefacts.zip`);


// ─── Marketing settings + GitHub importer ───
export const unpublishMarketingArticle = (id) =>
  api.post(`/marketing/articles/${id}/unpublish`).then(r => r.data);
export const verifyMarketingArticleLive = (id) =>
  api.post(`/marketing/articles/${id}/verify-live`).then(r => r.data);
export const getMarketingContentCheck = (id) =>
  api.get(`/marketing/articles/${id}/content-check`).then(r => r.data);
export const getMarketingPublicAudit = () =>
  api.get('/marketing/public-api/audit').then(r => r.data);
export const migrateMarketingPublicApi = () =>
  api.post('/marketing/public-api/migrate').then(r => r.data);
export const getMarketingSettings = () =>
  api.get('/marketing/settings').then(r => r.data);
export const saveMarketingSettings = (data) =>
  api.put('/marketing/settings', data).then(r => r.data);
export const testMarketingGitHub = () =>
  api.post('/marketing/github/test').then(r => r.data);
export const importMarketingArticles = () =>
  api.post('/marketing/import').then(r => r.data);


// ─── Marketing scheduling ───
export const scheduleMarketingArticle = (id, { scheduledFor, socialTargets }) =>
  api.post(`/marketing/articles/${id}/schedule`, { scheduledFor, socialTargets }).then(r => r.data);
export const unscheduleMarketingArticle = (id) =>
  api.post(`/marketing/articles/${id}/unschedule`).then(r => r.data);
export const getMarketingSchedule = () =>
  api.get('/marketing/schedule').then(r => r.data);


// ─── LinkedIn OAuth ───
export const linkedinStatus = () =>
  api.get('/marketing/linkedin/status').then(r => r.data);
export const linkedinLogin = () =>
  api.get('/marketing/linkedin/login').then(r => r.data);
export const linkedinDisconnect = () =>
  api.post('/marketing/linkedin/disconnect').then(r => r.data);
export const linkedinDiscoverOrg = () =>
  api.post('/marketing/linkedin/discover-org').then(r => r.data);
export const linkedinPreflight = () =>
  api.post('/marketing/linkedin/preflight').then(r => r.data);
export const retrySocialPost = (id, confirmNotPosted = false) =>
  api.post(`/marketing/social-posts/${id}/retry`, { confirmNotPosted }).then(r => r.data);


// ─── Unscheduled → scheduled promotion ───
// Reasons the admin picks when parking a WO in the backlog. Free text
// is allowed too; the picker just seeds the common cases so the UI
// stays consistent across articles.
export const WAITING_REASONS = [
  { value: 'parts',    label: 'Waiting for parts' },
  { value: 'customer', label: 'Waiting for customer' },
  { value: 'info',     label: 'Waiting for info / drawings' },
  { value: 'access',   label: 'Waiting for site access' },
  { value: 'other',    label: 'Other (see notes)' },
];
export const scheduleWorkOrder = (id, { dueDate, assigneeId, assigneeIds, startByDate, finishByDate }) => {
  const body = { dueDate };
  if (assigneeIds && assigneeIds.length) body.assigneeIds = assigneeIds;
  else if (assigneeId) body.assigneeId = assigneeId;
  if (startByDate) body.startByDate = startByDate;
  if (finishByDate) body.finishByDate = finishByDate;
  return api.post(`/work-orders/${id}/schedule`, body).then(r => r.data);
};


// ─── Lutrom Academy ───
// Reusable course engine — Portal admin uploads .zip packages,
// employees run them, certificates issue automatically. No AI or
// Emergent development credits required at runtime.
export const academyImport = (zipFile) => {
  const fd = new FormData();
  fd.append('file', zipFile);
  return api.post('/academy/courses/import', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};
export const academyListCourses = () =>
  api.get('/academy/courses').then(r => r.data);
export const academyGetCourse = (courseId, version) =>
  api.get(`/academy/courses/${courseId}/${version}`).then(r => r.data);
export const academyPublish = (courseId, version, change_summary = '') =>
  api.post(`/academy/courses/${courseId}/${version}/publish`, { change_summary }).then(r => r.data);
export const academyArchive = (courseId, version) =>
  api.post(`/academy/courses/${courseId}/${version}/archive`).then(r => r.data);
export const academyNewDraft = (courseId, version, new_version) =>
  api.post(`/academy/courses/${courseId}/${version}/new-draft`, { new_version }).then(r => r.data);
export const academyAssign = (courseId, version, body) =>
  api.post(`/academy/courses/${courseId}/${version}/assign`, body).then(r => r.data);
export const academyDedupeAssignments = () =>
  api.post('/academy/assignments/dedupe').then(r => r.data);
export const academyDeleteCertificate = (certNumber) =>
  api.delete(`/academy/certificates/${encodeURIComponent(certNumber)}`).then(r => r.data);

// Employee Hub ------------------------------------------------------
export const employeeHubDashboard = () => api.get('/employee/me/hub/dashboard').then(r => r.data);
export const myTraining = () => api.get('/employee/me/training').then(r => r.data);
export const uploadExternalCert = (fields, file) => {
  const fd = new FormData();
  Object.entries(fields || {}).forEach(([k, v]) => fd.append(k, v == null ? '' : String(v)));
  fd.append('file', file);
  return api.post('/employee/me/external-certs', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};
export const deleteExternalCert = (id) => api.delete(`/employee/me/external-certs/${id}`).then(r => r.data);
export const externalCertFileUrl = (id) => `${BASE}/employee/external-certs/${id}/file`;
export const openExternalCert = (id, filename) =>
  academyOpenAuthenticated(`/employee/external-certs/${id}/file`, filename || 'certificate');

// Employee Hub — Wave 2 (My Timesheets + corrections) ---------------
export const myTimesheets = (from, to) => {
  const params = {};
  if (from) params.from_ = from;
  if (to) params.to = to;
  return api.get('/employee/me/timesheets', { params }).then(r => r.data);
};
export const submitTimesheetCorrection = (body) =>
  api.post('/employee/me/timesheet-corrections', body).then(r => r.data);
export const cancelTimesheetCorrection = (id) =>
  api.delete(`/employee/me/timesheet-corrections/${id}`).then(r => r.data);
export const adminListTimesheetCorrections = (status = 'pending') =>
  api.get('/admin/timesheet-corrections', { params: { status } }).then(r => r.data);
export const adminApproveCorrection = (id, body = {}) =>
  api.post(`/admin/timesheet-corrections/${id}/approve`, body).then(r => r.data);
export const adminRejectCorrection = (id, body = {}) =>
  api.post(`/admin/timesheet-corrections/${id}/reject`, body).then(r => r.data);

// Employee Hub — Wave 3 (My Documents) ------------------------------
export const myDocuments = () => api.get('/employee/me/documents').then(r => r.data);
export const myDocumentOpen = (id, filename) =>
  academyOpenAuthenticated(`/employee/me/documents/${id}/file`, filename || 'document');
export const acknowledgeDocument = (id) =>
  api.post(`/employee/me/documents/${id}/acknowledge`).then(r => r.data);
export const adminListEmployeeDocuments = (userId) =>
  api.get('/admin/employee-documents', { params: userId ? { userId } : {} }).then(r => r.data);
export const adminUploadEmployeeDocument = (fields, file) => {
  const fd = new FormData();
  Object.entries(fields || {}).forEach(([k, v]) => fd.append(k, v == null ? '' : (typeof v === 'boolean' ? String(v) : String(v))));
  fd.append('file', file);
  return api.post('/admin/employee-documents', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};
export const adminDeleteEmployeeDocument = (id) =>
  api.delete(`/admin/employee-documents/${id}`).then(r => r.data);
export const adminOpenEmployeeDocument = (id, filename) =>
  academyOpenAuthenticated(`/admin/employee-documents/${id}/file`, filename || 'document');

// Learner
export const academyMyTraining = () =>
  api.get('/academy/my-training').then(r => r.data);
export const academySaveProgress = (courseId, version, body) =>
  api.post(`/academy/progress/${courseId}/${version}`, body).then(r => r.data);
export const academySubmitAttempt = (courseId, version, body) =>
  api.post(`/academy/attempts/${courseId}/${version}`, body).then(r => r.data);
export const academyAcknowledge = (courseId, version, body) =>
  api.post(`/academy/acknowledgements/${courseId}/${version}`, body).then(r => r.data);

// Asset URL — signed via bearer auth on the fetch itself; images/audio
// tags need this served via a fetch → blob URL, OR through the axios
// baseURL — we return the raw path and expect the caller to attach the
// access token via axios `api.get`.
export const academyAssetUrl = (courseId, version, path) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/academy/assets/${courseId}/${version}/${path}`;
// Authenticated fetch → Blob. The Academy asset endpoint requires a
// Bearer token, which the browser cannot attach to `<img src>`, so the
// player must fetch through axios and use the resulting object URL.
// The caller is responsible for revoking the returned URL.
export const academyAssetBlobUrl = async (courseId, version, path) => {
  const resp = await api.get(
    `/academy/assets/${courseId}/${version}/${path}`,
    { responseType: 'blob' },
  );
  return URL.createObjectURL(resp.data);
};
// Open an authenticated Academy endpoint (certificate PDF, export .zip,
// etc.) in a new tab safely across mobile Safari and desktop.
//
// We MUST call `window.open()` synchronously inside the user's click
// handler — iOS Safari's popup blocker rejects windows opened after an
// `await`. We open `about:blank` immediately, then swap its location to
// the authenticated blob once the fetch resolves. Fallback: if the
// popup was blocked (`win == null`) we trigger a same-tab download via
// a hidden anchor with the `download` attribute so mobile browsers save
// the file locally instead of silently dropping the request.
//
// The blob URL is revoked after 60 s — long enough for the tab/download
// to grab it, short enough not to leak memory across many clicks.
export const academyOpenAuthenticated = async (path, filename) => {
  const win = window.open('about:blank', '_blank');
  try {
    const resp = await api.get(path, { responseType: 'blob' });
    const objUrl = URL.createObjectURL(resp.data);
    if (win && !win.closed) {
      win.location.href = objUrl;
    } else {
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = filename || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(objUrl), 60_000);
    return true;
  } catch (e) {
    if (win && !win.closed) win.close();
    throw e;
  }
};
export const academyDelete = (courseId, version) =>
  api.delete(`/academy/courses/${courseId}/${version}`).then(r => r.data);
export const academyCertPdfUrl = (certNumber) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/academy/certificates/${certNumber}/pdf`;
// Open a certificate PDF via authenticated fetch. `certNumber` shows in
// the filename so downloaded copies are identifiable at rest.
export const academyOpenCertificatePdf = (certNumber) =>
  academyOpenAuthenticated(
    `/academy/certificates/${certNumber}/pdf`,
    `lutrom-certificate-${certNumber}.pdf`,
  );
// Admin-only preview certificate — server never touches DB or seq.
export const academyOpenPreviewCertificate = (courseId, version) =>
  academyOpenAuthenticated(
    `/academy/preview/${courseId}/${version}/certificate`,
    `PREVIEW-${courseId}-v${version}.pdf`,
  );
// Learner progress hydration + per-completion flag endpoints.
export const academyGetProgress = (courseId, version) =>
  api.get(`/academy/progress/${courseId}/${version}`).then(r => r.data);
export const academyAudioComplete = (courseId, version, screenId) =>
  api.post(`/academy/progress/${courseId}/${version}/audio-complete`, { screenId })
    .then(r => r.data);
export const academyInteractionComplete = (courseId, version, screenId) =>
  api.post(`/academy/progress/${courseId}/${version}/interaction-complete`, { screenId })
    .then(r => r.data);
// Bulk certificates ZIP for a (courseId, version). `employeeIds === null`
// means "every employee with a cert for this version". An empty array
// short-circuits server-side to a 404 rather than exporting everyone —
// matches the "no employees in filter" case exactly.
//
// Uses the same Safari-safe popup pattern as academyOpenAuthenticated
// but POSTs the JSON body instead of GETing.
export const academyBulkCertificatesZip = async (courseId, version, employeeIds) => {
  const win = window.open('about:blank', '_blank');
  try {
    const resp = await api.post(
      '/academy/certificates/bulk-zip',
      { courseId, version, employeeIds: employeeIds ?? null },
      { responseType: 'blob' },
    );
    const url = URL.createObjectURL(resp.data);
    const count = resp.headers?.['x-cert-count'] || '';
    if (win && !win.closed) {
      win.location.href = url;
    } else {
      const a = document.createElement('a');
      a.href = url;
      a.download = `${courseId}-v${version}-certificates.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return { count };
  } catch (e) {
    if (win && !win.closed) win.close();
    // Backend puts the human-readable reason in a text/json blob when
    // responseType is 'blob'. Read it back so the caller can toast it.
    if (e?.response?.data instanceof Blob) {
      try {
        const text = await e.response.data.text();
        try { e.response.data = JSON.parse(text); } catch (_) { e.response.data = { detail: text }; }
      } catch (_) { /* leave as-is */ }
    }
    throw e;
  }
};

// Phase 2 — export, editor, matrix, reassignment, notification settings
export const academyExportUrl = (courseId, version) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/academy/courses/${courseId}/${version}/export`;
// Authenticated download of the export .zip. Filename is derived from
// the course id + version so multiple exports on disk stay identifiable.
export const academyOpenExportZip = (courseId, version) =>
  academyOpenAuthenticated(
    `/academy/courses/${courseId}/${version}/export`,
    `${courseId}-v${version}.zip`,
  );
export const academyEditDraft = (courseId, version, patch) =>
  api.patch(`/academy/courses/${courseId}/${version}/content`, patch).then(r => r.data);
export const academyRoundtripCheck = (courseId, version) =>
  api.post(`/academy/courses/${courseId}/${version}/roundtrip-check`).then(r => r.data);
export const academyReassignAfterPublish = (courseId, newVersion, mode, user_ids) =>
  api.post(`/academy/courses/${courseId}/${newVersion}/reassign`, { mode, user_ids }).then(r => r.data);
export const academyTrainingMatrix = () =>
  api.get('/academy/training-matrix').then(r => r.data);
export const academyGetNotifSettings = () =>
  api.get('/academy/notification-settings').then(r => r.data);
export const academyPutNotifSettings = (body) =>
  api.put('/academy/notification-settings', body).then(r => r.data);
export const academyRunNotifSweep = () =>
  api.post('/academy/notification-sweep').then(r => r.data);

